import type { Cents } from "./money";
import type { QuantityUnit } from "./feature";

/**
 * Material Catalog (Phase 6).
 *
 *   Material Definition   generic requirement an Assembly speaks in
 *        └── Product      specific purchasable item (retailer OR manual/local)
 *              └── Price Observation   append-only price history, many sources
 *   Supplier              structured vendor entity (no credentials ever)
 *   Project Product Selection   a project's choice for one assembly material
 *        component + a full COPY-ON-USE commercial snapshot
 *
 * Assemblies stay retailer-neutral (they reference definitions); a project
 * picks the Product. Catalog edits never rewrite project estimates or
 * purchase history. Products stay tenant-ready: preferences/prices are
 * separate relationships so an organization_id can be added later.
 */

export type SupplierAccountType = "retail" | "pro" | "wholesale" | "trade" | "other";

export const SUPPLIER_ACCOUNT_TYPES: ReadonlyArray<{ id: SupplierAccountType; displayName: string }> = [
  { id: "retail", displayName: "Retail" },
  { id: "pro", displayName: "Pro / Account" },
  { id: "wholesale", displayName: "Wholesale" },
  { id: "trade", displayName: "Trade" },
  { id: "other", displayName: "Other" },
];

export interface Supplier {
  id: string;
  name: string;
  accountType: SupplierAccountType;
  website: string;
  notes: string;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MaterialDefinition {
  id: string;
  name: string;
  /** The construction unit requirements are expressed in (sqft, each, lf…). */
  constructionUnit: QuantityUnit;
  categoryId: string | null;
  notes: string;
  isSystem: boolean;
  sortOrder: number;
  archivedAt: string | null;
}

export type ProductStatus = "active" | "discontinued" | "archived";

export interface Product {
  id: string;
  materialDefinitionId: string;
  name: string;
  brand: string;
  sku: string;
  supplierId: string | null;
  /** What one purchased unit is called (case, each, box, gallon…). */
  purchaseUnit: QuantityUnit;
  /** Construction units covered by ONE purchase unit (×100); e.g. 23.64 sqft/case -> 2364. */
  coveragePerUnitX100: number;
  /** Construction unit of the coverage (normally the definition's unit). */
  coverageUnit: QuantityUnit;
  productUrl: string;
  status: ProductStatus;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProductAttribute {
  productId: string;
  key: string;
  value: string;
  sortOrder: number;
}

export type PriceSourceType =
  | "manual"
  | "retail"
  | "pro"
  | "wholesale"
  | "quote"
  | "historical-paid"
  | "imported";

export const PRICE_SOURCE_TYPES: ReadonlyArray<{ id: PriceSourceType; displayName: string }> = [
  { id: "manual", displayName: "Manual (unverified)" },
  { id: "retail", displayName: "Retail" },
  { id: "pro", displayName: "Pro / Account" },
  { id: "wholesale", displayName: "Wholesale" },
  { id: "quote", displayName: "Quote" },
  { id: "historical-paid", displayName: "Historical paid" },
  { id: "imported", displayName: "Imported" },
];

export type OfferAvailability = "in-stock" | "limited" | "out-of-stock" | "unknown";

export const OFFER_AVAILABILITIES: ReadonlyArray<{ id: OfferAvailability; displayName: string }> = [
  { id: "in-stock", displayName: "In Stock" },
  { id: "limited", displayName: "Limited" },
  { id: "out-of-stock", displayName: "Out of Stock" },
  { id: "unknown", displayName: "Unknown" },
];

/**
 * A price observation IS an Offer (spec §26A, Phase 9B): one product may
 * have many simultaneous offers (retailer / supplier / quote), each a
 * market observation scoped by supplier, location/market, price basis and
 * time. Offers never mutate Product, and changing offers never silently
 * reprices a project — selections snapshot copy-on-use.
 */
export interface PriceObservation {
  id: string;
  productId: string;
  supplierId: string | null;
  /** Price per ONE purchase unit, integer cents. */
  priceCents: Cents;
  priceIncludesTax: boolean;
  sourceType: PriceSourceType;
  /** ISO date observed/verified. */
  observedAt: string;
  validUntil: string | null;
  /** Free-text context (e.g. "Tyler store"); NOT structured location identity. */
  contextNote: string;
  notes: string;
  /** Structured market scope (ZIP); null = not location-specific. */
  marketZip: string | null;
  availability: OfferAvailability;
  /** Offer-specific product page URL (falls back to the product's URL). */
  offerUrl: string;
  /** Supplier's own SKU/model identifier for this offer, when different. */
  supplierSku: string;
  /** NULL = manually recorded. Set ONLY by a real provider import — manual
   *  or historical data is never presented as live provider data. */
  providerId: string | null;
  createdAt: string;
}

/**
 * The project's choice for one assembly material component. Everything
 * commercial is SNAPSHOTTED so the estimate is immune to catalog changes
 * until the user explicitly updates. Previous selections are kept with
 * replacedAt for history.
 */
export interface ProjectProductSelection {
  id: string;
  componentId: string;
  productId: string;
  priceObservationId: string | null;
  productNameSnapshot: string;
  supplierNameSnapshot: string;
  purchaseUnitSnapshot: QuantityUnit;
  coveragePerUnitX100Snapshot: number;
  coverageUnitSnapshot: QuantityUnit;
  priceCentsSnapshot: Cents;
  priceIncludesTaxSnapshot: boolean;
  priceSourceSnapshot: PriceSourceType;
  priceObservedAtSnapshot: string | null;
  /** Opt this selection out of pooled purchasing (own rounding group). */
  roundSeparately: boolean;
  /** Non-destructive purchase-quantity override (whole units); calculated preserved. */
  purchaseQtyOverride: number | null;
  createdAt: string;
  replacedAt: string | null;
}

export function validateSupplier(name: string): string[] {
  return name.trim() === "" ? ["Supplier name is required."] : [];
}

export function validateProduct(p: {
  name: string;
  materialDefinitionId: string;
  coveragePerUnitX100: number;
}): string[] {
  const problems: string[] = [];
  if (p.name.trim() === "") problems.push("Product name is required.");
  if (p.materialDefinitionId.trim() === "") problems.push("A material definition is required.");
  if (!Number.isSafeInteger(p.coveragePerUnitX100) || p.coveragePerUnitX100 <= 0) {
    problems.push("Coverage per purchase unit must be a positive number.");
  }
  return problems;
}

export function validateObservation(o: { priceCents: number; observedAt: string }): string[] {
  const problems: string[] = [];
  if (!Number.isSafeInteger(o.priceCents) || o.priceCents < 0) problems.push("Price must be a valid amount.");
  if (o.observedAt.trim() === "") problems.push("Observed date is required.");
  return problems;
}
